import { MouseEvent, useState } from 'react';
import styled, { css } from 'styled-components';
import BatchKillButtons from '#/components/buttons/BatchKillButtons';
import { blackPearl, blackTangaroa } from '@paradigm/design-system/src/colors';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import ArrowDown from '@paradigm/design-system/src/assets/ArrowDown';
import { useUMPlatformKillDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import { useTrackedCallback } from '@paradigm/features/src/analytics';
import { useBatchKill } from './hooks';

export default function BatchKillQuoteActions() {
  const isPlatformKillDisabled = useUMPlatformKillDisabled();
  const batchKill = useBatchKill();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const onKillBids = useTrackedCallback('kill desk bids', () =>
    batchKill('BUY'),
  );
  const onKillAsks = useTrackedCallback('kill desk asks', () =>
    batchKill('SELL'),
  );
  const onKillAll = useTrackedCallback('kill all', () => batchKill(null));

  const toggle = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((state) => !state);
  };

  if (isMobile) {
    return (
      <>
        <Container>
          <Button onClick={toggle}>
            Kill
            <ArrowIcon isOpen={open} width="12px" fill="#fff" />
          </Button>
          <Actions isOpen={open}>
            <BatchKillButtons
              disabled={isPlatformKillDisabled}
              css="display: block;"
              onKillBids={onKillBids}
              onKillAsks={onKillAsks}
              onKillAll={onKillAll}
            />
          </Actions>
        </Container>
        {open && <Backdrop onClick={toggle} />}
      </>
    );
  }
  return (
    <BatchKillButtons
      disabled={isPlatformKillDisabled}
      onKillBids={onKillBids}
      onKillAsks={onKillAsks}
      onKillAll={onKillAll}
    />
  );
}

const Container = styled.div`
  position: relative;
`;

const ArrowIcon = styled(ArrowDown).withConfig<{ isOpen: boolean }>({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'none')};
`;

const Actions = styled.div<{ isOpen: boolean }>`
  ${(props) =>
    props.isOpen
      ? css`
          background-color: ${blackPearl};
          padding: 0 1.25rem;
          width: 200px;
          border-radius: 8px;
          display: block;
          z-index: 100;
          position: absolute;
          top: 40px;
          right: 10px;
        `
      : css`
          display: none;
        `}
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
  z-index: 99;
  background-color: transparent;
`;

const Button = styled.button`
  opacity: 0.8;
  box-sizing: border-box;
  height: 34px;
  width: 66px;
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  background: transparent;
  position: relative;
  color: #ffffff;
  border: 2px solid ${blackTangaroa};
  border-radius: 3px;

  font-size: 1rem;
  letter-spacing: 0;
  line-height: 1rem;
`;
